import { DatePipe, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError } from 'rxjs/operators';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { MainTrafficConviction, TrafficConviction } from 'src/app/shared/models/traffic-convictions';
import { ROUTE_DQF, ROUTE_DQF_UPLOADS } from 'src/app/shared/routes';
import { DriverApiService } from 'src/app/shared/services/driver-api.service';
import { environment } from 'src/environments/environment';
import { SubSink } from 'subsink';

import { of } from 'rxjs';
import { CANADA_PROVINCES, USA_PROVINCES } from 'src/app/shared/models/provinces';
import { PENALTY } from 'src/app/shared/models/traffic-penalty';
import { VIOLATION_TYPES } from 'src/app/shared/models/traffic-violation-types';
import { MatRadioChange } from '@angular/material/radio';
const ACTION_SAVE_CONVICTIONS = 'Save Traffic Convictions';
const ACTION_UPDATE_CONVICTIONS = 'UPDATE Traffic Convictions';
const DELETE_MESSAGE = 'Are you sure you want to delete the Record?';
const REQUIRED_DATE_MESSAGE = 'Select Date';
const REQUIRED_VIOLATION_TYPE_MESSAGE = 'Select Violation Type';
const REQUIRED_STATE_MESSAGE = 'Select State of Violation Location';
const REQUIRED_PENALTY_MESSAGE = 'Select Penalty';
const REQUIRED_DEMERIT_MESSAGE = 'Select Demerit Points';
const DASH_NOT_ALLOW_AT_BEGINING = 'Dash and space is not allowed at begining';
const NUMBERS_SPEICAL_CHARACTERS_NOT_ALLOWED = 'Only alphabets, hyphen, apostrophe and space are allowed';

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/dd/yyyy'
  },
  display: {
    dateInput: 'MM/dd/yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM yyyy'
  }
};
@Component({
  selector: 'app-traffic-convictions',
  templateUrl: './traffic-convictions.component.html',
  styleUrls: ['./traffic-convictions.component.scss'],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    DatePipe
  ],
  standalone: false
})
export class TrafficConvictionsComponent implements OnInit {
  openForm = false;
  radioSaving: boolean = false;
  errorMessage: string;
  noTrafficConvictionsRecordsForm: UntypedFormGroup;
  trafficConvictionForm: UntypedFormGroup;
  disableNoTrafficConvictions: boolean;
  violationTypes = VIOLATION_TYPES;
  penalty = PENALTY;
  demeritPoints = [0, 1, 2, 3, 4, 5];
  maxDate = new Date();
  disableAddNewRecord = false;
  hasExplicitSelection = false;
  loading = false;
  stateOfViolation = CANADA_PROVINCES.concat(USA_PROVINCES);
  isContentReady: boolean = false;
  private subs = new SubSink();
  trafficRecords: TrafficConviction[];
  noConvinctionRecord: MainTrafficConviction;

  constructor(
    private readonly registrationService: RegistrationService,
    private readonly location: Location,
    private readonly driverApiService: DriverApiService,
    private readonly timeService: DateTimeService,
    private readonly fb: UntypedFormBuilder,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute
  ) {
    this.errorMessage = undefined;
    this.trafficConvictionForm = this.createForm();
    this.noTrafficConvictionsRecordsForm = this.createNoTrafficConvictionsRecordsForm();
  }

  ngOnInit(): void {
    this.loadTrafficConvictionsRecords();
    this.onViolationTypeChanged();
  }

  getConvictionRadioValue(): string | null {
    const noConvictions = this.noTrafficConvictionsRecordsForm.get('noConvictions').value;
    if (noConvictions === true) return 'no';
    if (this.trafficRecords?.length > 0) return 'yes';
    if (this.hasExplicitSelection && noConvictions === false) return 'yes';
    if (this.noConvinctionRecord && noConvictions === false) return 'yes';
    return null;
  }

  onConvictionRadioChange(event: MatRadioChange): void {
    this.hasExplicitSelection = true;
    this.radioSaving = true;
    if (event.value === 'no') {
      this.noTrafficConvictionsRecordsForm.get('noConvictions').setValue(true);
      this.noTrafficConvictionsRecordsForm.markAsDirty();
      this.disableAddNewRecord = true;
      this.onSubmitTrafficConvictionRecord(true, true).then(() => {
        this.radioSaving = false;
        this.goBack();
      }).catch(() => { this.radioSaving = false; });
    } else {
      this.noTrafficConvictionsRecordsForm.get('noConvictions').setValue(false);
      this.noTrafficConvictionsRecordsForm.markAsDirty();
      this.disableAddNewRecord = false;
      this.onSubmitTrafficConvictionRecord(true, true).then(() => {
        this.radioSaving = false;
      }).catch(() => { this.radioSaving = false; });
    }
  }

  onViolationTypeChanged() {
    this.trafficConvictionForm.get('violationType').valueChanges.subscribe(value => {
      this.updateValidators();
    });
  }

  updateValidators() {
    const vilationTypeValue = this.trafficConvictionForm.get('violationType')?.value;
    const otherViolationTypeControl = this.trafficConvictionForm.get('otherViolationType');

    if (vilationTypeValue === 'other') {
      otherViolationTypeControl.setValidators([Validators.required, Validators.pattern("^[A-Za-z-'\\s]+$")]);
      otherViolationTypeControl.setValue('');
    } else {
      otherViolationTypeControl.clearValidators();
      otherViolationTypeControl.setValue('');
    }
    otherViolationTypeControl.updateValueAndValidity();
  }

  loadTrafficConvictionsRecords(): void {
    this.isContentReady = false;
    this.trafficRecords = [];
    this.subs.add(
      this.registrationService.getTrafficConvictions().subscribe((response: any) => {
        if (response?.length) {
          response.forEach(res => {
            if (!res.hasOwnProperty('noConvictions')) {
              this.trafficRecords.push(res);
            } else {
              this.noConvinctionRecord = res;
            }
          });
          if (this.noConvinctionRecord) {
            this.noTrafficConvictionsRecordsForm.patchValue(this.noConvinctionRecord);
            this.disableAddNewRecord =
              this.noConvinctionRecord && this.noConvinctionRecord.noConvictions ? true : false;
          }
          if (this.trafficRecords && this.trafficRecords.length > 0) {
            this.disableNoTrafficConvictions = true;
          } else {
            this.disableNoTrafficConvictions = false;
          }
        }
        this.isContentReady = true;
      })
    );
  }

  onAddNewTrafficConvictionRecord(): void {
    this.openForm = true;
    this.disableAddNewRecord = true;
    this.disableNoTrafficConvictions = true;
  }

  onCancel(): void {
    this.openForm = false;
    this.disableNoTrafficConvictions = false;
    this.disableAddNewRecord = false;
    this.trafficConvictionForm.reset();
  }

  goBack(): void {
    const isFromDQF = this.isNavigatedFromDQF();
    if (isFromDQF) {
      // Navigate back to DQF uploads page
      this.router.navigateByUrl(`${ROUTE_DQF}/${ROUTE_DQF_UPLOADS}?type=Driver Uploads`);
    } else {
      this.location.back();
    }
  }

  private isNavigatedFromDQF(): boolean {
    // Check if we came from DQF by looking at the referrer or route history
    const referrer = document.referrer;

    // Check if referrer contains DQF routes
    if (referrer && (referrer.includes('/driverQualification/') || referrer.includes('/dqf/'))) {
      return true;
    }

    // Check if there's a DQF context in session storage or navigation state
    const navigationState = this.router.getCurrentNavigation()?.extras?.state;
    if (navigationState && navigationState['fromDQF']) {
      return true;
    }

    // Check if we have DQF-related query params or state
    if (this.activatedRoute.snapshot.queryParams['fromDQF'] ||
      this.activatedRoute.snapshot.queryParams['dqfId']) {
      return true;
    }

    return false;
  }

  onSubmitTrafficConvictionRecord(value: boolean, fromRadio = false): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!fromRadio) {
        this.isContentReady = false;
      }
      const model = this.prepareModel(value);
      if (model && model.id == null) {
        delete model.id;
        this.subs.add(
          this.registrationService
            .saveTrafficConviction(model)
            .pipe(
              catchError((error: any) => {
                this.errorMessage = environment.errorMessage;
                this.isContentReady = true;
                reject();
                return of(false);
              })
            )
            .subscribe((response: any) => {
              this.errorMessage = undefined;
              this.disableAddNewRecord = false;
              this.openForm = false;
              this.loadTrafficConvictionsRecords();
              this.trafficConvictionForm.reset();
              this.isContentReady = true;
              resolve();
            })
        );
      } else {
        let convictionId = model.id;
        delete model.id;
        this.subs.add(
          this.registrationService
            .updateTrafficConviction(model, convictionId)
            .pipe(
              catchError((error: any) => {
                this.errorMessage = environment.errorMessage;
                this.isContentReady = true;
                reject();
                return of(false);
              })
            )
            .subscribe((response: any) => {
              this.errorMessage = undefined;
              this.disableAddNewRecord = false;
              this.openForm = false;
              this.loadTrafficConvictionsRecords();
              this.trafficConvictionForm.reset();
              this.isContentReady = true;
              resolve();
            })
        );
      }
    });
  }

  prepareModel(value): MainTrafficConviction | TrafficConviction {
    if (value) {
      return new MainTrafficConviction(this.noTrafficConvictionsRecordsForm.value);
    } else {
      return new TrafficConviction(this.trafficConvictionForm.value);
    }
  }

  onEdit(record: TrafficConviction): void {
    this.onAddNewTrafficConvictionRecord();
    this.populateForm(record);
    this.disableAddNewRecord = true;
    this.trafficConvictionForm.markAsDirty();
  }

  onDelete(record: TrafficConviction): void {
    if (confirm(DELETE_MESSAGE)) {
      this.isContentReady = false;
      this.registrationService
        .deleteTrafficConviction(record.id)
        .pipe(
          catchError((error: any) => {
            this.errorMessage = environment.errorMessage;
            this.isContentReady = true;
            return of(false);
          })
        )
        .subscribe(response => {
          this.loadTrafficConvictionsRecords();
        });
    }
  }
  getViolationValue(value): string {
    const foundType = this.violationTypes.find(type => type.value === value);
    return foundType ? foundType.name : 'Other';
  }

  getConvictionDateErrorMessage(): string {
    return this.trafficConvictionForm.get('convictionDate')?.errors?.required ? REQUIRED_DATE_MESSAGE : '';
  }

  getViolationTypeErrorMessage(): string {
    return this.trafficConvictionForm.get('violationType')?.errors?.required ? REQUIRED_VIOLATION_TYPE_MESSAGE : '';
  }

  getStateOfViolationErrorMessage(): string {
    return this.trafficConvictionForm.get('stateOfViolation')?.errors?.required ? REQUIRED_STATE_MESSAGE : '';
  }

  getPenaltyErrorMessage(): string {
    return this.trafficConvictionForm.get('penalty')?.errors?.required ? REQUIRED_PENALTY_MESSAGE : '';
  }

  getDemeritPointsErrorMessage(): string {
    return this.trafficConvictionForm.get('demeritPoints')?.errors?.required ? REQUIRED_DEMERIT_MESSAGE : '';
  }

  getOtherViolationTypeErrorMessage() {
    const errors = this.trafficConvictionForm.get('otherViolationType').errors;
    if (errors) {
      if (errors.required) {
        return 'Select Other Violation Details';
      }
      if (errors.pattern) {
        const specialCharactersAtBeginig = new RegExp(/^[-'\s]/);
        if (this.trafficConvictionForm.get('otherViolationType').value.match(specialCharactersAtBeginig)) {
          return DASH_NOT_ALLOW_AT_BEGINING;
        } else {
          return NUMBERS_SPEICAL_CHARACTERS_NOT_ALLOWED;
        }
      }
    }
    return '';
  }

  private createNoTrafficConvictionsRecordsForm(): UntypedFormGroup {
    const form = this.fb.group({
      id: [''],
      noConvictions: [false]
    });
    return form;
  }

  private createForm(): UntypedFormGroup {
    return this.fb.group({
      id: '',
      convictionDate: ['', Validators.required],
      violationType: ['', Validators.required],
      otherViolationType: [''],
      stateOfViolation: ['', Validators.required],
      penalty: ['', Validators.required],
      demeritPoints: ['', Validators.required],
      notes: [
        '',
        [Validators.maxLength(140), Validators.pattern(/^[a-zA-Z0-9*&().,;#@!$-\s]*$/)]
      ]
    });
  }

  private populateForm(record: TrafficConviction): void {
    const recordedViolationType = VIOLATION_TYPES.find(v => v.value === record.violationType);

    this.trafficConvictionForm.patchValue({
      id: record?.id,
      convictionDate: record.convictionDate,
      violationType: recordedViolationType ? record.violationType : 'other',
      otherViolationType: recordedViolationType ? '' : record.violationType,
      stateOfViolation: record.stateOfViolation,
      penalty: record.penalty,
      demeritPoints: record.demeritPoints,
      notes: record.notes || ''
    })
  }
}
